import { useEffect, useMemo, useState, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Calendar, Clock, Users, Phone, Mail, Sparkles, MessageSquare,
  CheckCircle2, XCircle, CircleDot, CircleCheck, Trash2, Filter, RefreshCcw
} from "lucide-react";
import {
  listReservations,
  updateReservationStatus,
  deleteReservation,
} from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const STATUSES = [
  { key: "pending",   label: "Pending",   icon: CircleDot,     color: "text-amber-300",  border: "border-amber-300/50",  bg: "bg-amber-300/10" },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2,  color: "text-primary",    border: "border-primary/60",    bg: "bg-primary/10"    },
  { key: "completed", label: "Completed", icon: CircleCheck,   color: "text-emerald-400",border: "border-emerald-400/40",bg: "bg-emerald-400/10"},
  { key: "cancelled", label: "Cancelled", icon: XCircle,       color: "text-muted-foreground", border: "border-border",  bg: "bg-muted/40"      },
];

const statusMeta = (key) => STATUSES.find((s) => s.key === key) || STATUSES[0];

const FILTERS = [
  { key: "all", label: "All" },
  ...STATUSES.map((s) => ({ key: s.key, label: s.label })),
];

function safeDate(dateStr) {
  try {
    return format(parseISO(dateStr + "T00:00:00"), "EEE, d MMM yyyy");
  } catch {
    return dateStr;
  }
}

export default function AdminReservations() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [delDlg, setDelDlg] = useState({ open: false, id: null, name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listReservations();
      setReservations(data);
    } catch {
      toast.error("Failed to load reservations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return reservations;
    return reservations.filter((r) => (r.status || "pending") === filter);
  }, [reservations, filter]);

  const counts = useMemo(() => {
    const c = { all: reservations.length };
    STATUSES.forEach((s) => (c[s.key] = 0));
    reservations.forEach((r) => {
      const k = r.status || "pending";
      if (c[k] !== undefined) c[k] += 1;
    });
    return c;
  }, [reservations]);

  const selected = useMemo(
    () => reservations.find((r) => r.id === selectedId) || null,
    [reservations, selectedId]
  );

  async function changeStatus(id, status) {
    try {
      const updated = await updateReservationStatus(id, status);
      setReservations((rs) => rs.map((r) => (r.id === id ? { ...r, ...updated } : r)));
      toast.success(`Marked ${statusMeta(status).label.toLowerCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  }

  async function confirmDelete() {
    try {
      await deleteReservation(delDlg.id);
      setReservations((rs) => rs.filter((r) => r.id !== delDlg.id));
      if (selectedId === delDlg.id) setSelectedId(null);
      toast.success("Reservation deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDelDlg({ open: false, id: null, name: "" });
    }
  }

  return (
    <div data-testid="page-admin-reservations">
      <main className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
        {/* Header */}
        <div className="flex items-end justify-between gap-8 mb-10">
          <div>
            <div className="overline">Reservations</div>
            <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
              Tonight's bookings.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              Review incoming reservations. Confirm, complete, or cancel — status changes save instantly.
            </p>
          </div>
          <button
            data-testid="admin-reservations-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.72rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
          >
            <RefreshCcw size={12} strokeWidth={1.5} />
            Refresh
          </button>
        </div>

        {/* Filter chips */}
        <div className="mb-8 flex flex-wrap items-center gap-2 border-y border-border py-4">
          <Filter size={12} strokeWidth={1.5} className="text-muted-foreground mr-2" />
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`admin-reservations-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[0.68rem] uppercase tracking-[0.2em] border transition-colors ${
                  active
                    ? "border-primary text-primary bg-primary/10"
                    : "border-border text-foreground/70 hover:text-foreground hover:border-foreground/50"
                }`}
              >
                {f.label}
                <span className="text-muted-foreground tabular-nums">
                  {counts[f.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div data-testid="reservations-loading" className="py-24 text-center text-muted-foreground italic serif text-xl">
            <span className="ornament">Loading reservations</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <Calendar size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              {filter === "all" ? "No reservations yet." : `No ${filter} reservations.`}
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            {/* List */}
            <div className="lg:col-span-7 space-y-3">
              {filtered.map((r) => {
                const st = statusMeta(r.status || "pending");
                const StatusIcon = st.icon;
                const active = selectedId === r.id;
                return (
                  <button
                    key={r.id}
                    data-testid={`admin-reservation-row-${r.id}`}
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left border p-5 transition-all duration-200 bg-card ${
                      active
                        ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="serif text-xl text-foreground truncate">{r.name}</div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar size={12} strokeWidth={1.5} />
                            {safeDate(r.date)}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock size={12} strokeWidth={1.5} />
                            {r.time}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Users size={12} strokeWidth={1.5} />
                            {r.party_size}
                          </span>
                          {r.occasion && (
                            <span className="inline-flex items-center gap-1.5 text-primary/80">
                              <Sparkles size={12} strokeWidth={1.5} />
                              {r.occasion}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.65rem] uppercase tracking-[0.18em] border ${st.border} ${st.color} ${st.bg} shrink-0`}>
                        <StatusIcon size={10} strokeWidth={1.5} />
                        {st.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detail panel */}
            <aside className="lg:col-span-5">
              <div className="sticky top-40 border border-border bg-card p-6">
                {!selected ? (
                  <div className="py-16 text-center text-muted-foreground text-sm">
                    Select a reservation to view details.
                  </div>
                ) : (
                  <div data-testid="reservation-detail" className="fade-up">
                    <div className="overline">Ref · {selected.id.slice(0, 8).toUpperCase()}</div>
                    <h2 className="mt-3 serif text-3xl font-light text-foreground leading-tight">
                      {selected.name}
                    </h2>

                    <div className="mt-6 space-y-3 text-sm text-foreground/85">
                      <div className="flex items-center gap-3">
                        <Calendar size={14} strokeWidth={1.5} className="text-primary" />
                        {safeDate(selected.date)} · {selected.time}
                      </div>
                      <div className="flex items-center gap-3">
                        <Users size={14} strokeWidth={1.5} className="text-primary" />
                        {selected.party_size} {selected.party_size === 1 ? "guest" : "guests"}
                      </div>
                      <a href={`mailto:${selected.email}`} className="flex items-center gap-3 hover:text-primary transition-colors">
                        <Mail size={14} strokeWidth={1.5} className="text-primary" />
                        {selected.email}
                      </a>
                      <a href={`tel:${selected.phone}`} className="flex items-center gap-3 hover:text-primary transition-colors">
                        <Phone size={14} strokeWidth={1.5} className="text-primary" />
                        {selected.phone}
                      </a>
                      {selected.occasion && (
                        <div className="flex items-center gap-3">
                          <Sparkles size={14} strokeWidth={1.5} className="text-primary" />
                          {selected.occasion}
                        </div>
                      )}
                    </div>

                    {selected.notes && (
                      <div className="mt-6 border-t border-border pt-6">
                        <div className="overline flex items-center gap-2">
                          <MessageSquare size={11} strokeWidth={1.5} /> Notes
                        </div>
                        <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                          {selected.notes}
                        </p>
                      </div>
                    )}

                    <div className="mt-8 border-t border-border pt-6">
                      <div className="overline mb-3">Status</div>
                      <Select
                        value={selected.status || "pending"}
                        onValueChange={(v) => changeStatus(selected.id, v)}
                      >
                        <SelectTrigger
                          data-testid="reservation-status-trigger"
                          className="w-full bg-transparent border border-border rounded-none text-foreground focus:ring-0 focus:border-primary"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border border-border">
                          {STATUSES.map((s) => (
                            <SelectItem
                              key={s.key}
                              value={s.key}
                              data-testid={`reservation-status-option-${s.key}`}
                            >
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                      <button
                        data-testid="reservation-quick-confirm"
                        onClick={() => changeStatus(selected.id, "confirmed")}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-[0.7rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 transition-colors"
                      >
                        <CheckCircle2 size={12} strokeWidth={1.5} /> Confirm
                      </button>
                      <button
                        data-testid="reservation-quick-cancel"
                        onClick={() => changeStatus(selected.id, "cancelled")}
                        className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.7rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-destructive hover:text-destructive transition-colors"
                      >
                        <XCircle size={12} strokeWidth={1.5} /> Cancel
                      </button>
                      {isAdmin && (
                        <button
                          data-testid="reservation-delete"
                          onClick={() => setDelDlg({ open: true, id: selected.id, name: selected.name })}
                          className="ml-auto inline-flex items-center gap-2 px-3 py-2.5 text-muted-foreground hover:text-destructive transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </main>

      <AlertDialog
        open={delDlg.open}
        onOpenChange={(v) => setDelDlg((s) => ({ ...s, open: v }))}
      >
        <AlertDialogContent
          data-testid="reservation-delete-dialog"
          className="bg-card border border-border rounded-none text-foreground"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="serif text-2xl font-light">
              Delete reservation?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm">
              Permanently remove the booking for {delDlg.name}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-border bg-transparent hover:bg-accent text-foreground/80">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reservation-delete-confirm"
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
